import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import $ from "jquery";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Scatter } from "react-chartjs-2";
import API from "../../../../helpers/endpointService";
import { VesselStatus, inspectionSystem } from "../../../Constants";
import { getShipTypeList } from "../../../../actions/Lookup";
import styles from "../../../../Assets/jss/customStyle";
import withRouter from "../../../custom/withRouter";
import {
  EMPTY_HISTOGRAM_DATA,
  EMPTY_SCATTER_DATA,
  GRADE_RANGE_LEGEND,
  getHistogramScaleBounds,
  getHistogramPercentageScaleBounds,
  getScatterGradeScaleBounds,
  getScatterAgeScaleBounds,
  formatScatterAgeTickLabel,
  mapAheadGradeApiResponse,
  SCATTER_COLORS,
} from "./compareAheadGradeData";
import "./CompareAheadGrade.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

const chartPlotBackgroundPlugin = {
  id: "compareChartPlotBackground",
  beforeDraw(chart) {
    if (!chart.options.plugins?.compareChartPlotBackground?.enabled) return;

    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.fillStyle = "#f5f7f9";
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top
    );
    ctx.restore();
  },
};


const verticalLinePlugin = {
  id: "compareVerticalLines",
  defaults: {
    vesselGrade: null,
    averageGrade: null,
  },
  afterDraw(chart) {
    const pluginOpts = chart.options.plugins?.compareVerticalLines || {};
    const vesselGrade = parseInt(pluginOpts.vesselGrade, 10);
    const averageGrade = parseInt(pluginOpts.averageGrade, 10);
    if (Number.isNaN(vesselGrade) && Number.isNaN(averageGrade)) return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale || !chartArea) return;

    const drawVerticalLine = (value, color, dashed) => {
      const x = xScale.getPixelForValue(value);
      if (x < chartArea.left - 2 || x > chartArea.right + 2) return;

      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = dashed ? 1.5 : 2.5;
      if (dashed) ctx.setLineDash([6, 4]);
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    };

    const getLineX = (value) => {
      const x = xScale.getPixelForValue(value);
      if (x < chartArea.left - 2 || x > chartArea.right + 2) return null;
      return x;
    };

    const vesselX = !Number.isNaN(vesselGrade) ? getLineX(vesselGrade) : null;
    const averageX = !Number.isNaN(averageGrade) ? getLineX(averageGrade) : null;
    const labelsOverlap = vesselX != null && averageX != null && Math.abs(vesselX - averageX) < 80;

    const drawTopLabel = (x, label, color, fontWeight, topOffset) => {
      ctx.save();
      ctx.fillStyle = color;
      ctx.font = `${fontWeight} 11px 'Open Sans', sans-serif`;

      const padding = 6;
      const textWidth = ctx.measureText(label).width;
      const halfWidth = textWidth / 2;
      let textX = x;
      let textAlign = "center";

      if (x + halfWidth > chartArea.right - padding) {
        textAlign = "right";
        textX = chartArea.right - padding;
      } else if (x - halfWidth < chartArea.left + padding) {
        textAlign = "left";
        textX = chartArea.left + padding;
      }

      ctx.textAlign = textAlign;
      ctx.fillText(label, textX, chartArea.top + topOffset);
      ctx.restore();
    };

    if (!Number.isNaN(averageGrade) && averageX != null) {
      drawVerticalLine(averageGrade, "#9e9e9e", true);
      drawTopLabel(
        averageX,
        `Average Grade: ${averageGrade}`,
        "#666666",
        "normal",
        labelsOverlap ? 28 : 14
      );
    }
    if (!Number.isNaN(vesselGrade) && vesselX != null) {
      drawVerticalLine(vesselGrade, "#1b3a57", false);
      drawTopLabel(
        vesselX,
        `Your Grade: ${vesselGrade}`,
        "#1b3a57",
        "bold",
        14
      );
    }
  },
};

const horizontalLinePlugin = {
  id: "compareHorizontalLine",
  defaults: {
    averageGrade: null,
  },
  afterDraw(chart) {
    if (chart.config.type !== "scatter") return;

    const pluginOpts = chart.options.plugins?.compareHorizontalLine || {};
    const averageGrade = parseInt(pluginOpts.averageGrade, 10);
    if (Number.isNaN(averageGrade)) return;

    const { ctx, chartArea, scales } = chart;
    const yScale = scales.y;
    if (!yScale || !chartArea) return;

    const y = yScale.getPixelForValue(averageGrade);
    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = "#9e9e9e";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(chartPlotBackgroundPlugin, verticalLinePlugin, horizontalLinePlugin);

const APP_FONT = "'Open Sans', sans-serif";
const CHART_AXIS_FONT = { family: APP_FONT, size: 11, weight: "600" };
const CHART_TICK_FONT = { family: APP_FONT, size: 11 };
const CHART_AXIS_COLOR = "#666666";

class CompareAheadGrade extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobile: false,
      vesselGrade: 0,
      vesselAge: -1,
      vesselTypeLabel: "vessel",
      ageRangeLabel: "",
      averageGrade: 0,
      scatterAverageGrade: 0,
      histogramData: EMPTY_HISTOGRAM_DATA,
      scatterData: EMPTY_SCATTER_DATA,
      hasSectorData: false,
      hasAgeData: false,
      loadError: false,
      loading: true,
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(getShipTypeList(VesselStatus.ShipType));
    this.loadVesselData();
    window.addEventListener("resize", this.resize);
    this.resize();
    this.highlightInterval = setInterval(this.highlightSidebar, 10);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resize);
    clearInterval(this.highlightInterval);
    $("#CAG1 .MuiListItemText-primary").addClass("spanTopBar");
    $("#CompareAheadGrade1 .MuiListItemText-primary").removeClass("fw-bolder");
    $("#CompareAheadGrade").addClass("d-none");
    $("#CompareAheadGrade").removeClass("d-block");
    $("#CAGL").removeClass("active-color");
    $("#CAGT").removeClass("fw-bolder");
  }

  highlightSidebar = () => {
    $("#CAG1 .MuiListItemText-primary").addClass("spanTopBar");
    $("#CompareAheadGrade1 .MuiListItemText-primary").addClass("fw-bolder");
    $("#CompareAheadGrade").removeClass("d-none");
    $("#CompareAheadGrade").addClass("d-block");
    $("#CAGL").addClass("active-color");
    $("#CAGT").addClass("fw-bolder");
  };

  resize = () => {
    this.setState({ isMobile: window.innerWidth < 850 });
  };

  loadVesselData = async () => {
    const shipId = parseInt(localStorage.getItem("DashId"), 10);
    const inspectionId = parseInt(localStorage.getItem("InspectionId"), 10);
    if (!shipId || !inspectionId) {
      this.setState({ loading: false });
      return;
    }

    this.props.dispatch({ type: "SHOW_LOADER", data: true });

    try {
      const aheadGradeRes = await axios.get(
        `${API.Get_AheadGrade}?ShipId=${shipId}&InspectionId=${inspectionId}&SectionId=${inspectionSystem.Vesselparticulars}`
      );

      const mapped = mapAheadGradeApiResponse(aheadGradeRes?.data);
      this.setState({
        vesselGrade: mapped.vesselGrade,
        vesselAge: mapped.vesselAge,
        vesselTypeLabel: mapped.vesselTypeLabel,
        ageRangeLabel: mapped.ageRangeLabel,
        averageGrade: mapped.averageGrade,
        scatterAverageGrade: mapped.scatterAverageGrade,
        histogramData: mapped.histogramData,
        scatterData: mapped.scatterData,
        hasSectorData: mapped.hasSectorData,
        hasAgeData: mapped.hasAgeData,
        loadError: false,
        loading: false,
      });
    } catch (error) {
      this.setState({
        vesselGrade: 0,
        vesselAge: -1,
        vesselTypeLabel: "vessel",
        ageRangeLabel: "",
        averageGrade: 0,
        scatterAverageGrade: 0,
        histogramData: EMPTY_HISTOGRAM_DATA,
        scatterData: EMPTY_SCATTER_DATA,
        hasSectorData: false,
        hasAgeData: false,
        loadError: true,
        loading: false,
      });
    } finally {
      this.props.dispatch({ type: "SHOW_LOADER", data: false });
    }
  };

  sidebar = () => {
    this.props.navigate("/sidebar");
  };

  goBack = () => {
    this.props.navigate("/shiplist");
  };

  renderHistogramKeyPanel() {
    return (
      <div className="compare-key-box">
        <span className="spandoc-thead compare-key-box-title">KEY</span>
        <div className="compare-hist-key-lines">
          <div className="compare-hist-key-line-item">
            <span className="spandoc-td">Your Grade</span>
            <span className="compare-hist-key-line-solid" />
          </div>
          <div className="compare-hist-key-line-item">
            <span className="spandoc-td">Average Grade</span>
            <span className="compare-hist-key-line-dashed compare-hist-key-line-dashed-grey" />
          </div>
        </div>
        <span className="compare-grade-range-label">Grade range</span>
        <div className="compare-grade-range-row">
          {GRADE_RANGE_LEGEND.map((item) => (
            <div className="compare-grade-range-item" key={item.label}>
              <span className="compare-key-square" style={{ background: item.color }} />
              <span className="spandoc-td">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderScatterKeyPanel() {
    return (
      <div className="compare-key-box">
        <span className="spandoc-thead compare-key-box-title">KEY</span>
        <div className="compare-scatter-key-grid">
          <div className="compare-scatter-key-item">
            <span className="spandoc-td">Your grade</span>
            <span className="compare-scatter-key-dot" style={{ background: "#1b3a57" }} />
          </div>
          <div className="compare-scatter-key-item">
            <span className="spandoc-td">Average grade</span>
            <span className="compare-scatter-key-line-dashed compare-scatter-key-line-dashed-grey" />
          </div>
          <div className="compare-scatter-key-item">
            <span className="spandoc-td">All sector ships</span>
            <span className="compare-scatter-key-dot" style={{ background: "#bdbdbd" }} />
          </div>
          <div className="compare-scatter-key-item">
            <span className="spandoc-td">Age comparable ships</span>
            <span className="compare-scatter-key-dot" style={{ background: SCATTER_COLORS.ageComparable }} />
          </div>
        </div>
      </div>
    );
  }

  getHistogramOptions() {
    const { vesselGrade, averageGrade, histogramData } = this.state;
    const scaleBounds = getHistogramScaleBounds(
      histogramData.points,
      vesselGrade,
      averageGrade
    );
    const percentageScaleBounds = getHistogramPercentageScaleBounds(histogramData.points);

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: {
        padding: { top: 28, bottom: 22, left: 4, right: 56 },
      },
      parsing: {
        xAxisKey: "x",
        yAxisKey: "y",
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          filter: (item) => item.datasetIndex === 0,
          callbacks: {
            title: (items) => {
              const grade = items[0]?.raw?.x;
              return grade >= 90 ? "Ahead Grade: 90+" : `Ahead Grade: ${grade}`;
            },
            label: (item) => `${item.formattedValue}% of total ships`,
          },
        },
        compareChartPlotBackground: { enabled: true },
        compareVerticalLines: {
          vesselGrade,
          averageGrade,
        },
      },
      scales: {
        x: {
          type: "linear",
          min: scaleBounds.min,
          max: scaleBounds.max,
          offset: false,
          title: {
            display: true,
            text: "Ahead Grade",
            font: CHART_AXIS_FONT,
            color: CHART_AXIS_COLOR,
            padding: { top: 6 },
          },
          ticks: {
            values: scaleBounds.tickValues,
            maxRotation: 0,
            autoSkip: false,
            font: CHART_TICK_FONT,
            color: CHART_AXIS_COLOR,
            callback: (value) =>
              scaleBounds.tickValues.includes(value) ? value : undefined,
          },
          grid: { display: false },
          border: { display: true, color: "#dddddd" },
        },
        y: {
          title: {
            display: true,
            text: "Percentage of Total Ships",
            font: CHART_AXIS_FONT,
            color: CHART_AXIS_COLOR,
          },
          min: percentageScaleBounds.min,
          max: percentageScaleBounds.max,
          ticks: {
            stepSize: percentageScaleBounds.tickStep,
            maxRotation: 0,
            autoSkip: false,
            font: CHART_TICK_FONT,
            color: CHART_AXIS_COLOR,
            callback: (value) => {
              const rounded = Math.round(value);
              return rounded >= 0
                && rounded <= percentageScaleBounds.max
                && rounded % percentageScaleBounds.tickStep === 0
                ? rounded
                : undefined;
            },
          },
          grid: {
            color: "#e8e8e8",
            drawBorder: false,
          },
          border: { display: false },
        },
      },
    };
  }

  getScatterOptions() {
    const { scatterAverageGrade, scatterData, vesselGrade, vesselAge } = this.state;
    const scaleBounds = getScatterGradeScaleBounds(
      scatterData,
      vesselGrade,
      scatterAverageGrade
    );
    const ageScaleBounds = getScatterAgeScaleBounds();

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => `Age: ${item.raw.x.toFixed(1)} yrs, Ahead Grade: ${item.raw.y.toFixed(0)}`,
          },
        },
        compareHorizontalLine: { averageGrade: scatterAverageGrade },
      },
      layout: {
        padding: { top: 8, bottom: 8, left: 4, right: 8 },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Ship Age",
            font: CHART_AXIS_FONT,
            color: CHART_AXIS_COLOR,
          },
          min: ageScaleBounds.min,
          max: ageScaleBounds.max,
          ticks: {
            values: ageScaleBounds.tickValues,
            maxRotation: 0,
            autoSkip: false,
            font: CHART_TICK_FONT,
            color: CHART_AXIS_COLOR,
            callback: (value) => {
              const rounded = Math.round(value);
              return ageScaleBounds.tickValues.includes(rounded)
                ? formatScatterAgeTickLabel(rounded)
                : undefined;
            },
          },
          grid: { color: "#e8e8e8", drawBorder: false },
          border: { display: false },
        },
        y: {
          title: {
            display: true,
            text: "Ahead Grade",
            font: CHART_AXIS_FONT,
            color: CHART_AXIS_COLOR,
          },
          min: scaleBounds.min,
          max: scaleBounds.max,
          ticks: {
            values: scaleBounds.tickValues,
            maxRotation: 0,
            autoSkip: false,
            font: CHART_TICK_FONT,
            color: CHART_AXIS_COLOR,
            callback: (value) =>
              scaleBounds.tickValues.includes(value) ? value : undefined,
          },
          grid: { color: "#e8e8e8", drawBorder: false },
          border: { display: false },
        },
      },
    };
  }

  render() {
    const { menuToggle } = this.props;
    const {
      isMobile,
      vesselGrade,
      vesselTypeLabel,
      ageRangeLabel,
      histogramData,
      scatterData,
      hasSectorData,
      loadError,
      loading,
    } = this.state;

    const histogramChartData = {
      datasets: [
        {
          type: "bar",
          label: "Percentage of Total Ships",
          data: histogramData.points,
          backgroundColor: histogramData.barColors,
          borderColor: histogramData.barColors,
          borderWidth: 0,
          barThickness: 8,
          maxBarThickness: 10,
          order: 2,
        },
      ],
    };

    const scatterChartData = {
      datasets: [
        {
          label: "All sector ships",
          data: scatterData.allSectorShips,
          backgroundColor: SCATTER_COLORS.allSector,
          borderColor: SCATTER_COLORS.allSector,
          pointRadius: 4,
          pointHoverRadius: 5,
          order: 1,
        },
        {
          label: "Age comparable ships",
          data: scatterData.ageComparableShips,
          backgroundColor: SCATTER_COLORS.ageComparable,
          borderColor: SCATTER_COLORS.ageComparable,
          pointRadius: 4,
          pointHoverRadius: 5,
          order: 2,
        },
        {
          label: "Your grade",
          data: scatterData.vesselPoint ? [scatterData.vesselPoint] : [],
          backgroundColor: SCATTER_COLORS.vessel,
          borderColor: SCATTER_COLORS.vessel,
          pointRadius: 7,
          pointHoverRadius: 8,
          order: 3,
        },
      ],
    };

    return (
      <>
        {isMobile ? (
          <div className="row w-100 mt-4 mob-topfixed">
            <div className="col-6 mt-auto">
              <List className="pt-3 mt-4 pl-3" style={{ backgroundColor: "#F6F6F6" }}>
                <ListItem onClick={this.sidebar}>
                  <ListItemText id="CAG1" className="textNaviIcon" primary="Inspection Tasks" />
                  <NavigateNextIcon id="CAG" className="naviIcon ml-1" style={{ width: 23, height: 23 }} />
                  <Divider className="border-bottom" />
                </ListItem>
              </List>
            </div>
            <div className="col-6 pt-2 mt-5">
              <h4>Ahead Grade</h4>
            </div>
          </div>
        ) : null}

        <div
          className="ml-auto scroll-hide mt-138"
          style={{
            backgroundColor: "#FFFFFF",
            width: isMobile ? "100%" : menuToggle ? "calc(100% - 240px)" : "calc(100% - 64px)",
            marginTop: isMobile ? "" : -75,
          }}
        >
          <table className="dataTable" width="100%">
            <tbody>
              <tr>
                <td width="5%" className="border-right text-center">
                  <i className="fa fa-arrow-left cursorPointer" onClick={this.goBack} />
                </td>
                <td width="95%" />
              </tr>
            </tbody>
          </table>

          <div className="row" style={{ margin: isMobile ? "" : "5px" }}>
            <div className="row px-3">
              <div className="col-12 pt-2">
                {!isMobile ? <h6>Ahead Grade</h6> : null}
              </div>
            </div>
            <div
              className={
                isMobile ? "col-12 px-4 pt-2 mt-1 mx-1" : "col-12 px-3 pt-2 mt-1 mx-1"
              }
            >
              <p className="spandoc-td mb-3">
                This section of the report allows you to compare your ship&apos;s grade with similar ships.
              </p>

              {!loading && !hasSectorData ? (
                <p className="spandoc-td mb-3">
                  {loadError
                    ? "Unable to load Ahead Grade comparison data. Please try again later."
                    : vesselGrade > 0
                      ? `Your Grade: ${vesselGrade}. No sector comparison data is available for this vessel yet.`
                      : "No sector comparison data is available for this vessel yet."}
                </p>
              ) : null}

              {!loading && hasSectorData ? (
              <>
              <div className="compare-chart-block">
                <div className="row">
                  <div className="col-lg-8 col-md-12">
                    <span className="spandoc-thead1 d-block mb-2">
                      Your Ahead Grade vs other {vesselTypeLabel} vessels
                    </span>
                    <div className="compare-chart-panel" style={{ height: 360 }}>
                      {!loading && (
                        <Bar data={histogramChartData} options={this.getHistogramOptions()} />
                      )}
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-12">
                    <div className="compare-side-panel">
                      <p className="spandoc-td">
                        This graph shows the distribution of Ahead Grades against your ship&apos;s sector.
                      </p>
                      {this.renderHistogramKeyPanel()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="compare-chart-block">
                <div className="row">
                  <div className="col-lg-8 col-md-12">
                    <span className="spandoc-thead1 d-block mb-2">
                      Your Ahead Grade vs other {vesselTypeLabel} vessels, age {ageRangeLabel} years
                    </span>
                    <div className="compare-chart-panel" style={{ height: 360 }}>
                      {!loading && (
                        <Scatter data={scatterChartData} options={this.getScatterOptions()} />
                      )}
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-12">
                    <div className="compare-side-panel">
                      <p className="spandoc-td">
                        This graph shows your ship&apos;s Ahead Grade compared against other ships inspected
                        in the same sector, within a similar age range, and how it compares against the
                        average Ahead Grade for the sector.
                      </p>
                      {this.renderScatterKeyPanel()}
                    </div>
                  </div>
                </div>
              </div>
              </>
              ) : null}
            </div>
          </div>
        </div>
      </>
    );
  }
}

CompareAheadGrade.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  menuToggle: PropTypes.bool,
  shipType: PropTypes.array,
};

const mapStateToProps = (state) => ({
  menuToggle: state.commonReducer.menuToggle,
  shipType: state.lookupReducer.shipType || [],
});

export default connect(mapStateToProps)(withRouter(withStyles(styles)(CompareAheadGrade)));
